import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { comics, articles, categories } from '@/mocks/comics';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const comic = comics.find((c) => c.slug === slug);
  const article = articles.find((a) => a.slug === slug);
  const item = comic || article;

  if (!item) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">内容未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isComic = 'author' in item && 'chapters' in item;

  const category = isComic
    ? categories.find((c) => c.slug === (item as typeof comics[0]).category)
    : null;

  const breadcrumbItems = [
    { label: '首页', href: '/' },
  ];
  if (category) {
    breadcrumbItems.push({ label: category.name, href: `/category/${category.slug}` });
  }
  breadcrumbItems.push({ label: item.title });

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': isComic ? 'Article' : 'Article',
    headline: item.title,
    description: isComic ? (item as typeof comics[0]).description : (item as typeof articles[0]).excerpt,
    url: `http://rldcigr.com/article/${item.slug}`,
    author: {
      '@type': 'Person',
      name: isComic ? (item as typeof comics[0]).author : (item as typeof articles[0]).author,
    },
    datePublished: isComic ? '2024-01-01' : (item as typeof articles[0]).date,
  };

  const relatedComics = isComic
    ? comics.filter((c) => c.category === (item as typeof comics[0]).category && c.id !== item.id).slice(0, 4)
    : comics.slice(0, 4);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />

      <Navbar />
      <main>
        <section className="pt-24 pb-8 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </section>

        {isComic ? (
          <ArticleComicDetail comic={item as typeof comics[0]} />
        ) : (
          <ArticleContentDetail article={item as typeof articles[0]} />
        )}

        <section className="py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-950 mb-8" style={{ fontFamily: 'var(--font-heading)' }}>
              相关推荐
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-5">
              {relatedComics.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/article/${comic.slug}`}
                  className="group cursor-pointer"
                >
                  <div className="relative rounded-lg overflow-hidden bg-background-50 mb-3 aspect-[3/4]">
                    <img
                      src={comic.coverImage}
                      alt={comic.title}
                      title={`${comic.title} - 韩漫在线阅读`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs font-semibold">
                      ★ {comic.rating}
                    </div>
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1 line-clamp-1 group-hover:text-primary-500 transition-colors">{comic.title}</h3>
                  <p className="text-xs text-foreground-500">{comic.author}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function ArticleComicDetail({ comic }: { comic: typeof comics[0] }) {
  return (
    <section className="pb-16 px-4 md:px-6 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row gap-8">
          <div className="md:w-[300px] flex-shrink-0">
            <div className="rounded-lg overflow-hidden bg-background-100 aspect-[3/4] sticky top-24">
              <img
                src={comic.coverImage}
                alt={comic.title}
                title={`${comic.title} - 免费韩漫大全`}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
              {comic.title}
            </h1>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="text-sm text-foreground-600">作者：{comic.author}</span>
              <span className="text-foreground-300">|</span>
              <span className={`text-sm font-medium ${comic.status === '已完结' ? 'text-accent-600' : 'text-primary-600'}`}>{comic.status}</span>
              <span className="text-foreground-300">|</span>
              <span className="text-sm text-foreground-600">共 {comic.chapters} 话</span>
              <span className="text-foreground-300">|</span>
              <span className="text-sm text-foreground-600"><i className="ri-eye-line mr-1"></i>{comic.views}</span>
            </div>

            <div className="flex items-center gap-1 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <i
                  key={star}
                  className={`ri-star-fill text-lg ${star <= Math.round(comic.rating / 2) ? 'text-accent-500' : 'text-foreground-300'}`}
                ></i>
              ))}
              <span className="ml-2 text-lg font-bold text-foreground-950">{comic.rating}</span>
            </div>

            <div className="flex flex-wrap gap-2 mb-6">
              {comic.tags.map((tag) => (
                <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
                  {tag}
                </span>
              ))}
            </div>

            <div className="bg-background-100 rounded-lg p-6 mb-6">
              <h2 className="text-base font-semibold text-foreground-950 mb-3">作品简介</h2>
              <p className="text-sm text-foreground-600 leading-relaxed">{comic.longDescription}</p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-book-open-line"></i>
                开始阅读
              </a>
              <a
                href="#"
                className="inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full border border-foreground-300 text-foreground-700 text-sm hover:border-primary-500 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-bookmark-line"></i>
                加入收藏
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ArticleContentDetail({ article }: { article: typeof articles[0] }) {
  return (
    <section className="pb-16 px-4 md:px-6 bg-background-50">
      <div className="max-w-3xl mx-auto">
        <div className="rounded-lg overflow-hidden mb-8">
          <img
            src={article.coverImage}
            alt={article.title}
            title={article.title}
            className="w-full aspect-[16/10] object-cover"
          />
        </div>

        <div className="flex items-center gap-3 mb-4">
          <span className="text-xs px-2.5 py-1 rounded-full bg-primary-100 text-primary-700">{article.category}</span>
          <span className="text-sm text-foreground-500">{article.date}</span>
          <span className="text-sm text-foreground-500">作者：{article.author}</span>
        </div>

        <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
          {article.title}
        </h1>

        <div className="prose prose-sm max-w-none text-foreground-700 leading-relaxed">
          {article.content.split('\n\n').map((paragraph, idx) => {
            const trimmed = paragraph.trim();
            if (!trimmed) return null;
            return (
              <p key={idx} className="mb-4 text-sm text-foreground-600 leading-relaxed">
                {trimmed}
              </p>
            );
          })}
        </div>

        <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-300/60">
          {article.tags.map((tag) => (
            <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs whitespace-nowrap">
              {tag}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}